import {InfoTooltip} from '@wandb/components';
import {Tag} from '@wandb/weave/components/Tag';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useCallback, useId} from 'react';

import {usePanelSearchDebouncedContext} from '../../../../state/panelbank/PanelSearchContextProvider';
import {useDefaultSectionMatchedKeys} from '../../../../state/panelGenHistoryKeys/panelGenHistoryKeysStore';
import {DD_PRIVACY_ALLOW} from '../../../../util/datadog/privacy';
import {useNgwTruncatedPanelGenLimits} from '../../../../util/dynamicConfigs/ngwTruncatedPanelGenLimits';
import {useIsNgwEnabled} from '../../../../util/rampFeatureFlags/rampFeatureFlags';
import {maybePluralize} from '../../../../util/uihelpers';
import {TruncateTextTooltip} from '../../../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../../../common/TruncateText/TruncateTextTypes';
import {EditableLabel} from '../../../elements/EditableLabel';
import {useWorkspaceSectionContext} from '../WorkspaceSectionContextProvider';

export const PanelBankSectionTitle = ({
  activePanelCount,
  isEditingName,
  isSearching,
  isSectionNameSearchMatch,
  panelSearchMatchCount,
  sectionName,
  setIsEditingName,
  updateName,
}: {
  activePanelCount: number;
  isEditingName: boolean;
  isSearching: boolean;
  isSectionNameSearchMatch: boolean;
  panelSearchMatchCount: number;
  sectionName: string;
  setIsEditingName: (editing: boolean) => void;
  updateName: (text: string) => void;
}) => {
  const {makeHighlightSearchTitle} = usePanelSearchDebouncedContext();

  const onBlur = useCallback(() => {
    setIsEditingName(false);
  }, [setIsEditingName]);

  const onClick = useCallback((e: React.SyntheticEvent) => {
    // we use the menu option to trigger editing mode, rather than direct click
    e.preventDefault();
  }, []);

  if (isEditingName) {
    return (
      <span
        className="panel-bank__section-title-content"
        data-dd-privacy={DD_PRIVACY_ALLOW}>
        <EditableLabel
          title={sectionName}
          onSave={updateName}
          onBlur={onBlur}
          editing={isEditingName}
          serverText={sectionName}
          onClick={onClick}
          dataTest={'panel-bank-section-title-label'}
        />
        <span data-test="panel-bank-section-panel-count">
          <PanelBankSectionCountLabel
            isSectionNameSearchMatch={isSectionNameSearchMatch}
            panelSearchMatchCount={panelSearchMatchCount}
            activePanelCount={activePanelCount}
            isSearching={isSearching}
          />
        </span>
      </span>
    );
  }

  return (
    <span
      className="panel-bank__section-title-content"
      data-dd-privacy={DD_PRIVACY_ALLOW}>
      <TruncateTextTooltip
        truncationType={TruncationType.End}
        className="text-left"
        noLink
        fullText={sectionName}
        highlight={makeHighlightSearchTitle}
        dataTest={'panel-bank-section-title-label'}
      />
      <PanelBankSectionCountLabel
        isSectionNameSearchMatch={isSectionNameSearchMatch}
        panelSearchMatchCount={panelSearchMatchCount}
        activePanelCount={activePanelCount}
        isSearching={isSearching}
      />
    </span>
  );
};

const PanelBankSectionCountLabel = ({
  isSectionNameSearchMatch,
  panelSearchMatchCount,
  activePanelCount,
  isSearching,
}: {
  isSectionNameSearchMatch: boolean;
  panelSearchMatchCount: number;
  activePanelCount: number;
  isSearching: boolean;
}) => {
  const isNgwEnabled = useIsNgwEnabled();
  const isFilteredByPanelSearch = isSearching && !isSectionNameSearchMatch;

  // check if section is auto-generated and has truncated keys
  const {defaultSectionName} = useWorkspaceSectionContext();
  const autoSectionMatchedKeys =
    useDefaultSectionMatchedKeys(defaultSectionName);
  if (autoSectionMatchedKeys?.isTruncated) {
    return (
      <PanelBankSectionTruncatedInfoTooltip
        displayedPanelCount={
          isFilteredByPanelSearch ? panelSearchMatchCount : activePanelCount
        }
        matchedKeyCount={autoSectionMatchedKeys.count}
      />
    );
  }

  const labelText =
    isFilteredByPanelSearch && !isNgwEnabled
      ? `${panelSearchMatchCount.toLocaleString()} of ${activePanelCount.toLocaleString()}`
      : panelSearchMatchCount.toLocaleString(); // when no search active, search match count equals active panel count

  return (
    <span data-test="panel-bank-section-panel-count">
      <Tag color="moon" label={labelText} />
    </span>
  );
};

const PanelBankSectionTruncatedInfoTooltip = ({
  displayedPanelCount,
  matchedKeyCount,
}: {
  displayedPanelCount: number;
  matchedKeyCount: number;
}) => {
  const tooltipId = useId();
  const {
    config: {maxMetricsPerGroup},
  } = useNgwTruncatedPanelGenLimits();
  return (
    // top-[0.66px] centers the info icon on the section name's cap height
    // rather than its line-box center. Flexbox centers the icon's box on the
    // line-box center, which reads high next to the text; the cap-height center
    // sits ~0.66px below that, so this nudge aligns the icon with uppercase and
    // Title-case names (lowercase reads marginally high).
    <span
      data-test="panel-bank-section-panel-count"
      className="relative top-[0.66px] flex items-center gap-4">
      <InfoTooltip
        id={tooltipId}
        aria-label="Section truncation details"
        placement="top"
        size="sm">
        <TailwindContents>
          <div className="flex max-w-[240px] flex-col gap-4 text-left">
            <span className="font-semibold">
              {maybePluralize(displayedPanelCount, 'panel', 's', true)} shown in
              this section.
            </span>
            <span className="text-wb-text-tertiary">
              This section contains{' '}
              {maybePluralize(matchedKeyCount, 'key', 's', true)} matching the
              current filters.
            </span>
            <span className="text-wb-text-tertiary">
              Up to {maxMetricsPerGroup.toLocaleString()} keys are
              auto-generated as panels per section. Use panel search to find
              keys beyond this limit.
            </span>
          </div>
        </TailwindContents>
      </InfoTooltip>
    </span>
  );
};
